import { useCallback, useId, useMemo } from 'react';

import { ColourLegend, MultiSelectDropdown, type MultiSelectOption, ToggleSwitch } from '~components';
import { useBlastDesignContext } from '../context';
import { CollarOrToeSelect } from './CollarOrToeSelect';
import { NeighbouringHoleColourBySelect } from './NeighbouringHoleColourBySelect';
import { RLRangeSlider } from './RLRangeSlider';

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Sidebar controls for neighbouring drill holes.
 * Reads all required state from BlastDesignContext to avoid prop drilling.
 */
const NeighbouringDrillHolesSection = () => {
  const {
    patternVisibility,
    setPatternVisibility,
    collarOrToe,
    setCollarOrToe,
    colourByMode,
    setColourByMode,
    rlRange,
    setRlRange,
    neighbouringHoleColourScale,
    blast,
    patternNames,
    validRLs,
    patternRlsByName,
    rlBounds,
    includeCurrentBlastHoles,
    setIncludeCurrentBlastHoles,
  } = useBlastDesignContext();

  const includeCurrentBlastHolesId = useId();

  const selectablePatternNames = useMemo(
    () => patternNames.filter((patternName) => patternName !== blast.name),
    [patternNames, blast.name],
  );

  const isPatternVisible = useCallback(
    (patternName: string) => patternVisibility[patternName] ?? true,
    [patternVisibility],
  );

  const isPatternInRange = useCallback(
    (patternName: string) => {
      if (!rlRange) return true;

      const rls = patternRlsByName.get(patternName) ?? [];
      return rls.some((rl) => rl >= rlRange[0] && rl <= rlRange[1]);
    },
    [rlRange, patternRlsByName],
  );

  const visiblePatternOptions = useMemo(
    () =>
      selectablePatternNames.map((patternName) => ({
        value: patternName,
        label: patternName,
        disabled: !isPatternInRange(patternName),
      })),
    [selectablePatternNames, isPatternInRange],
  );

  const selectedPatternValues = useMemo(
    () =>
      selectablePatternNames.filter((patternName) => isPatternInRange(patternName) && isPatternVisible(patternName)),
    [selectablePatternNames, isPatternInRange, isPatternVisible],
  );

  const handleVisiblePatternsChange = useCallback(
    (selectedValues: string[]) => {
      const selectedValueSet = new Set(selectedValues);

      for (const patternName of selectablePatternNames) {
        if (!isPatternInRange(patternName)) continue;
        setPatternVisibility(patternName, selectedValueSet.has(patternName));
      }
    },
    [selectablePatternNames, setPatternVisibility, isPatternInRange],
  );

  const getSummaryLabel = useCallback(
    ({ selectedOptions, allOptions }: { selectedOptions: MultiSelectOption[]; allOptions: MultiSelectOption[] }) => {
      const availableCount = allOptions.filter((option) => !option.disabled).length;
      const selectedCount = selectedOptions.length;
      const allOptionsAvailable = availableCount === allOptions.length;

      if (allOptionsAvailable && availableCount > 0 && selectedCount === availableCount) {
        return 'All selected';
      }

      return `${selectedCount} selected`;
    },
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-foreground text-xl">Neighbouring Drill Holes</h2>
      </div>
      <div className="space-y-3">
        <p className="font-bold text-base">Position</p>
        <CollarOrToeSelect selected={collarOrToe} onChange={setCollarOrToe} />
      </div>
      {rlBounds && (
        <div className="space-y-3">
          <p className="font-bold text-base">Blast RL Range</p>
          <RLRangeSlider
            min={rlBounds[0]}
            max={rlBounds[1]}
            value={rlRange ?? rlBounds}
            onChange={setRlRange}
            validRLs={validRLs}
          />
        </div>
      )}
      {selectablePatternNames.length > 0 && (
        <div className="space-y-3">
          <p className="font-bold text-base">Visible Blast Patterns</p>
          <MultiSelectDropdown
            label="Blasts"
            ariaLabel="Blast patterns to display"
            options={visiblePatternOptions}
            selectedValues={selectedPatternValues}
            onSelectedValuesChange={handleVisiblePatternsChange}
            placeholder="None selected"
            getSummaryLabel={getSummaryLabel}
          />
          <div className="flex items-center justify-between gap-2">
            <label className="font-medium text-sm" htmlFor={includeCurrentBlastHolesId}>
              Include current blast holes
            </label>
            <ToggleSwitch
              id={includeCurrentBlastHolesId}
              checked={includeCurrentBlastHoles}
              onCheckedChange={setIncludeCurrentBlastHoles}
              ariaLabel="Include current blast holes in neighbouring holes"
            />
          </div>
        </div>
      )}
      <div className="space-y-3">
        <NeighbouringHoleColourBySelect value={colourByMode} onChange={setColourByMode} />
      </div>
      {neighbouringHoleColourScale && (
        <ColourLegend
          colourScale={neighbouringHoleColourScale}
          isItemDimmed={(entry) => {
            if (typeof entry.value !== 'string') return false;
            if (colourByMode !== 'blast') return false;
            if (entry.value === blast.name) return !includeCurrentBlastHoles;
            return !isPatternInRange(entry.value) || !isPatternVisible(entry.value);
          }}
        />
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { NeighbouringDrillHolesSection };
